import { useAuthDispatches } from '@/app/_providers/store';
import { destroySession, getSession, setCookies, setSession } from '@/app/actions/auth/cookies.action';
import { TRegistrationStep } from '@/core/models';
import { TLogin, TUpdateSession } from '@/services/auth/auth.types';
import { getMe } from '@/services/user/user.service';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

const useAuthFunc = () => {
   const { login, logout, updateProfile } = useAuthDispatches();
   const { push } = useRouter();

   const redirect = useCallback(
      (step: TRegistrationStep) => {
         switch (step) {
            case 'PERSONAL_DETAILS':
               return push('/registration/personal-details');

            case 'PAYMENT':
               return push('/registration/payment');

            case 'KYC':
               return push('/registration/kyc');

            case 'APPLICATION_VERIFICATION':
               return push('/registration/application');

            case 'COMPLETED':
               return push('/view-card');

            case 'CLINIC_DETAILS':
               return push('/registration/clinic/details');

            case 'PARTNER_DETAILS':
               return push('/registration/partner/details');

            case 'CLINIC_PAYMENT':
               return push('/registration/clinic/payment');

            case 'PARTNER_PAYMENT':
               return push('/registration/partner/payment');

            default:
               return push('/');
         }
      },
      [push],
   );

   const handleLogin = useCallback(
      async (value: TLogin, refer?: string | null) => {
         await setCookies('session', value);
         login(value);

         if (refer) {
            return push(`/clinic-directory/${refer}`);
         }

         redirect(value.user.registration_step);
      },
      [login, redirect, push],
   );

   const handleLogout = useCallback(async () => {
      await destroySession();
      logout();
      push('/auth/login');
   }, [logout, push]);

   const updateSession = useCallback(
      async (value: Partial<TUpdateSession>) => {
         const session = await getSession();

         const payload = {
            ...session,
            ...value,
         };

         await setCookies('session', payload);
         updateProfile({ ...payload.user });
      },
      [updateProfile],
   );

   const refreshSession = useCallback(async () => {
      const session = await getSession();

      if (session) {
         const [error, data] = await getMe();

         if (error) {
            await destroySession();
            logout();
            return;
         }

         const payload = {
            ...session,
            user: data.response,
         };

         await setSession(payload);

         updateProfile({ ...data.response });
      } else {
         await destroySession();
         logout();
      }
   }, [logout, updateProfile]);

   return { handleLogin, handleLogout, updateSession, refreshSession, redirect };
};

export default useAuthFunc;
